import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { mockData } from '../data/mock';
import { MapPin, Phone, Mail, Instagram, Clock, ExternalLink } from 'lucide-react';

const Footer = () => {
  const { t, language } = useLanguage();

  const footerLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/kurse', label: t('nav.courses') },
    { path: '/ueber-uns', label: t('nav.about') },
    { path: '/bg-kurse', label: t('nav.bgCourses') },
    { path: '/kontakt', label: t('nav.contact') },
  ];

  const legalLinks = [
    { path: '/impressum', label: t('footer.imprint') },
    { path: '/datenschutz', label: t('footer.privacy') },
  ];

  return (
    <footer className="bg-neutral-900 text-white">
      {/* Main Footer */}
      <div className="container-custom section-padding">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <img
              src={mockData.images.logo}
              alt="Erste-Hilfe Fokus"
              className="h-16 w-auto mb-6 brightness-0 invert"
            />
            <p className="text-neutral-400 text-sm mb-6 italic">
              {t('footer.slogan')}
            </p>
            <div className="flex gap-4">
              <a
                href={mockData.company.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-neutral-800 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href={mockData.company.social.tiktok}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-neutral-800 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                aria-label="TikTok"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                </svg>
              </a>
              <a
                href={mockData.company.social.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-neutral-800 rounded-full flex items-center justify-center hover:bg-green-600 transition-colors"
                aria-label="WhatsApp"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-6">
              {language === 'de' ? 'Schnelllinks' : 'Quick Links'}
            </h4>
            <nav className="flex flex-col gap-3">
              {footerLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="text-neutral-400 hover:text-white transition-colors underline-effect w-fit"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold mb-6">{t('footer.contact')}</h4>
            <div className="flex flex-col gap-4">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div className="text-neutral-400 text-sm">
                  <p className="font-medium text-white">{mockData.company.address.street}</p>
                  <p>{mockData.company.address.zip} {mockData.company.address.city}</p>
                </div>
              </div>
              <a
                href={`tel:${mockData.company.phone}`}
                className="flex items-center gap-3 text-neutral-400 hover:text-white transition-colors"
              >
                <Phone className="w-5 h-5 text-red-500" />
                <span className="text-sm">0159 0226 8861</span>
              </a>
              <a
                href={`mailto:${mockData.company.emailInfo}`}
                className="flex items-center gap-3 text-neutral-400 hover:text-white transition-colors"
              >
                <Mail className="w-5 h-5 text-red-500" />
                <span className="text-sm">{mockData.company.emailInfo}</span>
              </a>
              <a
                href={`mailto:${mockData.company.email}`}
                className="flex items-center gap-3 text-neutral-400 hover:text-white transition-colors"
              >
                <Mail className="w-5 h-5 text-red-500" />
                <span className="text-sm">{mockData.company.email}</span>
              </a>
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-red-500" />
                <div className="text-sm text-neutral-400">
                  <p>{language === 'de' ? 'Alle Anfragen werden innerhalb von 24 Stunden beantwortet' : 'All inquiries are answered within 24 hours'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Partners & Certifications */}
          <div>
            <h4 className="text-lg font-semibold mb-6">{t('footer.partners')}</h4>
            <div className="space-y-4">
              <div className="bg-neutral-800 rounded-lg p-4">
                <p className="text-sm text-neutral-400 mb-2">BG Zulassung</p>
                <p className="text-white font-medium">Med1Plus.de - 8.0559</p>
              </div>
              <div className="flex items-center gap-2 text-neutral-400 text-sm">
                <ExternalLink className="w-4 h-4" />
                <span>DGUV 304-001 zertifiziert</span>
              </div>
              <div className="flex items-center gap-2 text-neutral-400 text-sm">
                <ExternalLink className="w-4 h-4" />
                <span>§19 FeV anerkannt</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-neutral-800">
        <div className="container-custom py-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-center md:text-left">
            <p className="text-neutral-500 text-sm">
              © {new Date().getFullYear()} Erste-Hilfe Fokus. {language === 'de' ? 'Alle Rechte vorbehalten.' : 'All rights reserved.'}
            </p>
            <p className="text-neutral-600 text-xs mt-1">
              Coded with love by{' '}
              <a 
                href="https://beta-webagentur.de" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-red-500 hover:text-red-400 transition-colors"
              >
                Beta-Webagentur.de
              </a>
            </p>
          </div>
          <div className="flex items-center gap-6">
            {legalLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="text-neutral-500 hover:text-white text-sm transition-colors"
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
